import React, { useEffect, useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { Loader2, Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let _stripePromise = null;
const getStripe = (publishable_key) => {
  if (!publishable_key) return null;
  if (!_stripePromise) _stripePromise = loadStripe(publishable_key);
  return _stripePromise;
};

const CARD_OPTIONS = {
  hidePostalCode: false,
  style: {
    base: {
      fontSize: '15px',
      color: '#18181b',
      fontFamily: '"Outfit", -apple-system, BlinkMacSystemFont, sans-serif',
      '::placeholder': { color: '#a1a1aa' },
    },
    invalid: { color: '#dc2626' },
  },
};

/**
 * Inner form — requires <Elements> wrapper from parent.
 * Props:
 *  - clientSecret: Stripe SetupIntent client_secret
 *  - cardholderName: display string
 *  - onSuccess(paymentMethodId): callback after card confirmed
 *  - onCancel: optional cancel handler
 *  - submitLabel: button text (default "Save Card")
 */
const InnerCardForm = ({ clientSecret, cardholderName, onSuccess, onCancel, submitLabel = 'Save Card' }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const card = elements.getElement(CardElement);
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card,
          billing_details: { name: cardholderName || undefined },
        },
      });
      if (result.error) {
        setError(result.error.message || 'Card could not be saved.');
        setSubmitting(false);
        return;
      }
      setSuccess(true);
      const pmId = result.setupIntent?.payment_method;
      if (onSuccess) await onSuccess(pmId);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8" data-testid="card-form-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
        <p className="font-bold text-zinc-900">Card saved successfully</p>
        <p className="text-sm text-zinc-500 mt-1">Your payment method is on file.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="card-form">
      <div className="border-2 border-zinc-200 rounded-xl p-3 bg-white focus-within:border-[#F56F05] transition-colors">
        <CardElement options={CARD_OPTIONS} />
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex gap-2 text-xs text-red-700" data-testid="card-form-error">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}
      <div className="flex gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting} className="rounded-xl flex-1" data-testid="card-form-cancel">
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={!stripe || submitting}
          className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl flex-1"
          data-testid="card-form-submit"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
          {submitLabel}
        </Button>
      </div>
      <p className="text-[11px] text-zinc-400 text-center flex items-center justify-center gap-1">
        <Lock className="h-3 w-3" /> Encrypted by Stripe. We never see your card number.
      </p>
    </form>
  );
};

/**
 * Outer wrapper — manages Stripe.js loading + provides <Elements> context.
 */
const StripeCardForm = ({ publishableKey, clientSecret, cardholderName, onSuccess, onCancel, submitLabel }) => {
  const stripePromise = useMemo(() => getStripe(publishableKey), [publishableKey]);
  if (!publishableKey) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700" data-testid="stripe-key-missing">
        Stripe configuration is missing. Please contact support.
      </div>
    );
  }
  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#F56F05]" />
      </div>
    );
  }
  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
      <InnerCardForm
        clientSecret={clientSecret}
        cardholderName={cardholderName}
        onSuccess={onSuccess}
        onCancel={onCancel}
        submitLabel={submitLabel}
      />
    </Elements>
  );
};

/**
 * Convenience wrapper that fetches its own SetupIntent for a signed-in client.
 * Used by the in-portal "Manage Card" flow.
 */
export const ClientStripeCardForm = ({ token, cardholderName, onSuccess, onCancel }) => {
  const [config, setConfig] = useState({ publishable_key: '' });
  const [clientSecret, setClientSecret] = useState('');
  const [setupIntentId, setSetupIntentId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cfg, intent] = await Promise.all([
          axios.get(`${API_URL}/api/payments/config`),
          axios.post(`${API_URL}/api/payments/setup-intent`,
            { origin_url: window.location.origin },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);
        if (!alive) return;
        setConfig(cfg.data);
        setClientSecret(intent.data.client_secret);
        setSetupIntentId(intent.data.setup_intent_id);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not start card setup');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#F56F05]" /></div>;
  }

  const handleSuccess = async (paymentMethodId) => {
    // Backend status endpoint persists the saved card as default
    try {
      await axios.get(
        `${API_URL}/api/payments/setup-intent/${setupIntentId}/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      console.warn('Status sync failed:', e);
    }
    if (onSuccess) await onSuccess(paymentMethodId);
  };

  return (
    <StripeCardForm
      publishableKey={config.publishable_key}
      clientSecret={clientSecret}
      cardholderName={cardholderName}
      onSuccess={handleSuccess}
      onCancel={onCancel}
    />
  );
};

export default StripeCardForm;
